import { ExpandMore } from '@mui/icons-material';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { getColor } from '../../assets/theme';
import useDisclosure from '../../hooks/useDisclosure';
import useLayoutContext from '../../hooks/useLayoutContext';
import { Navigation } from '../../interfaces/module.interface';

const disabledRouteTitle = [
  'Room Posting',
  'City Ledger',
  'Print Blank Reg Form',
  'Traces',
  'Declare New Day',
  'Cashier Breakdown',
  'End Shift',
  'Force End Shift',
  'Misc',
  'Reprint',
  'Void Invoice',
];

/**
 * Props for the NavBtn component
 * @extends Navigation
 */
interface NavBtnProps extends Navigation {
  toggleSidebar: () => void;
  setForceCloseAccordion: React.Dispatch<React.SetStateAction<boolean>>;
  forceCloseAccordion: boolean;
  onChildClick?: (title: string) => void;
}

/**
 * Props for the NavAccordionBtn component
 * @extends Navigation
 */
interface NavAccordionBtnProps extends Navigation {
  setForceCloseAccordion: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * NavAccordion component for rendering nested navigation items
 * @param {Object} props - The component props
 * @param {NavAccordionBtnProps} props.nav - The navigation item data
 * @param {() => void} props.toggleSidebar - Function to toggle the sidebar
 * @param {number} [props.level=1] - The nesting level of the accordion
 */
const NavAccordion = ({
  nav,
  level = 1,
  toggleSidebar,
  onChildClick,
}: {
  nav: NavAccordionBtnProps;
  level?: number;
  toggleSidebar: () => void;
  onChildClick?: (title: string) => void;
}) => {
  const accDisclosure = useDisclosure();
  const { closeSnackbar } = useSnackbar();
  const location = useLocation();

  const { primary } = getColor();

  const smallScreen = useMediaQuery('(max-width: 425px)');

  const { openComingSoon } = useLayoutContext();

  const handleNavLinkClick = () => {
    if (nav.link === '#') {
      openComingSoon();
    }
    nav.onClick && nav.onClick();
    // nav?.setForceCloseAccordion(true);
    smallScreen && toggleSidebar();
    closeSnackbar();
  };

  const paddingLeft = 26 + level * 30.5;

  const { isOpen: open } = accDisclosure;

  const titleRef = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    if (el) {
      setIsOverflowed(el.scrollWidth > el.clientWidth);
    }
  }, [nav.title, level]);

  return (
    <>
      <NavLink
        to={nav?.accordion || nav?.disabled ? '#' : nav.link}
        state={
          nav?.accordion || nav?.disabled
            ? { noRoute: true, from: location?.pathname }
            : { from: location?.pathname }
        }
        style={({ isActive }) => ({
          color: isActive && location.pathname === nav.link ? primary : 'unset',
          textDecoration: 'none',
        })}
        className='nav__a'
        onClick={handleNavLinkClick}
      >
        <Stack
          sx={{
            position: 'relative',
            mt: '4px',
            ':before': {
              content: '""',
              position: 'absolute',
              left: `${(level - 1) * 30 + (level - 1) * 2}px`,
              width: '22px',
              height: '12px',
              backgroundColor: '#E0E0E0',
              maskImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 14'%3E%3Cpath d='M1 1v5a7 7 0 0 0 7 7h20' stroke='black' stroke-width='3' fill='none'/%3E%3C/svg%3E\")",
              transform: `translate(calc(-30px * -1), calc(-22px * -0.4))`,
              maskRepeat: 'no-repeat',
            },
          }}
        >
          <ListItemButton
            sx={{
              height: '36px',
              ml: `${paddingLeft}px`,
              py: '18px',
              px: '12px',
              gap: '12px',
              bgcolor:
                location.pathname === nav.link || open ? `${primary}19` : '',
              borderRadius: '4px',
              mr: '2px',
              ' .nav__title': { color: open ? primary : 'default' },
              ':hover': { bgcolor: `${primary}19` },
              ':hover .nav__title': { color: primary },
            }}
            disableRipple
            // onClick={nav?.accordion && accDisclosure.onToggle} // old function
            onClick={() => {
              if (nav.accordion) {
                accDisclosure.onToggle();
                return;
              }
              onChildClick?.(nav.title); // sends the child upward
              handleNavLinkClick();
            }}
            disabled={nav?.disabled}
          >
            <ListItemText
              primary={
                isOverflowed ? (
                  <Tooltip title={nav.title}>
                    <span
                      ref={titleRef}
                      style={{
                        display: 'inline-block',
                        maxWidth: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {nav.title}
                    </span>
                  </Tooltip>
                ) : (
                  <span
                    ref={titleRef}
                    style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {nav.title}
                  </span>
                )
              }
              sx={{
                fontSize: '14px!important',
                margin: 'unset',
                marginBottom: '-3.5px',
                display: true ? 'block' : 'none',
                span: {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
              className='nav__title'
            />
            {nav?.accordion && true && (
              <ExpandMore
                sx={{
                  transform: `rotate(${
                    accDisclosure.isOpen ? '0deg' : '-90deg'
                  })`,
                  transition: 'transform 0.2s ease',
                }}
              />
            )}
          </ListItemButton>
        </Stack>
      </NavLink>
      {nav?.accordion && (
        <Collapse in={accDisclosure.isOpen} unmountOnExit>
          <List
            component='div'
            disablePadding
            sx={{
              position: 'relative',
              ':before': {
                top: 0,
                left: `${30 + level * 32}px`,
                bottom: 0,
                width: '2px',
                content: '""',
                position: 'absolute',
                bgcolor: '#E0E0E0',
                height: `calc(100% - 20px)`,
              },
            }}
          >
            {nav.accordion.map((nestedNav: any, i: any) => (
              <NavAccordion
                key={i}
                nav={nestedNav}
                level={level + 1}
                toggleSidebar={toggleSidebar}
                onChildClick={onChildClick}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

/**
 * NavBtn component for rendering navigation buttons
 * @param {NavBtnProps} data - The navigation button data
 */
const NavBtn = (data: NavBtnProps) => {
  const { darkMode, primary } = getColor();
  const {
    isOpen,
    toggleSidebar,
    accordion,
    link,
    title,
    icon,
    activeIcon,
    setForceCloseAccordion,
    forceCloseAccordion,
  } = data;
  const smallScreen = useMediaQuery('(max-width: 425px)');
  const { closeSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    !isOpen && toggleSidebar();
    setOpen(!open);
    setForceCloseAccordion(false);
  };

  const location = useLocation();
  const { openComingSoon } = useLayoutContext();

  useEffect(() => {
    !isOpen && setOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!forceCloseAccordion) return;
    setOpen(false);
    return () => {
      setForceCloseAccordion(false);
    };
  }, [forceCloseAccordion]);

  return (
    <NavLink
      id='nav-btn'
      to={
        title == 'Home'
          ? '/'
          : accordion || disabledRouteTitle.includes(title)
            ? '#'
            : data?.disabled
              ? '#'
              : link
      }
      state={
        accordion || disabledRouteTitle.includes(title) || data?.disabled
          ? { noRoute: true, from: location?.pathname }
          : { from: location?.pathname }
      }
      style={({ isActive }) => {
        return {
          cursor: data?.disabled ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
          textDecoration: 'none',
          color: isActive && location.pathname === link ? primary : 'unset',
        };
      }}
      className='nav__a'
      onClick={() => {
        if (data?.disabled) return;
        if (link === '#') {
          openComingSoon();
        }
        data.onClick && data.onClick();
        if (!open && accordion) return;
        // setForceCloseAccordion(true);
        smallScreen && toggleSidebar();
        closeSnackbar();
      }}
    >
      <Tooltip title={title} placement='right' disableHoverListener={isOpen}>
        <ListItemButton
          sx={{
            height: '46px',
            ml: 1,
            mr: '2px',
            borderRadius: '4px',
            pl: '12px',
            gap: '12px',
            bgcolor:
              (location.pathname === link ||
                location.pathname.includes(link) ||
                open) &&
              isOpen
                ? `${primary}19`
                : '',
            color:
              location.pathname === link ||
              location.pathname.includes(link) ||
              open
                ? primary
                : 'default',
            ':hover': {
              bgcolor:
                (isOpen && !open) || open ? `${primary}19` : 'transparent',
            },
            ':hover .nav__icon': {
              color: !isOpen || !open ? 'none' : primary,
              bgcolor: !isOpen || (!open && !isOpen) ? '#F5F5F5' : '',
            },
            ':hover .nav__title': { color: primary },
          }}
          disableRipple
          onClick={accordion && handleClick}
          disabled={data?.disabled}
        >
          <ListItemIcon
            sx={{
              minWidth: '20px',
              color:
                location.pathname === link ||
                location.pathname.includes(link) ||
                open
                  ? primary
                  : '',
              bgcolor: location.pathname === link && !isOpen ? '#EEF7FE' : '',
              padding: '6px',
              borderRadius: '4px',
              transition: 'background .3s ease',
            }}
            className='nav__icon'
          >
            <SvgIcon
              component={
                location.pathname === link || location.pathname.includes(link)
                  ? activeIcon
                  : icon
              }
            />
          </ListItemIcon>
          <ListItemText
            primary={title}
            sx={{
              fontSize: '14px!important',
              margin: 'unset',
              marginBottom: '-3.5px',
              display: isOpen ? 'block' : 'none',
            }}
            className='nav__title'
          />
          {accordion && isOpen && (
            <ExpandMore
              sx={{
                transform: `rotate(${open ? '0deg' : '-90deg'})`,
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
      {accordion && (
        <Collapse in={open} unmountOnExit>
          <List
            component='div'
            disablePadding
            sx={{
              bgcolor: darkMode ? '#0c0c0c' : '#fcfcfc',
              position: 'relative',
              ':before': {
                top: 0,
                left: 30,
                bottom: 0,
                width: '2px',
                height: `calc(100% - 20px)`,
                content: '""',
                position: 'absolute',
                bgcolor: '#E0E0E0',
              },
            }}
          >
            {accordion.map((nav, i) => {
              return (
                <NavAccordion
                  key={i}
                  nav={{ ...nav, setForceCloseAccordion }}
                  toggleSidebar={toggleSidebar}
                  onChildClick={data.onChildClick}
                />
              );
            })}
          </List>
        </Collapse>
      )}
    </NavLink>
  );
};

export default NavBtn;
